import React from "react";
import {
  Grid,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Box,
  Stack,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import { useTranslation } from "react-i18next";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import PublicIcon from "@mui/icons-material/Public";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const PAYMENT_METHOD_ICONS = {
  card: <CreditCardIcon fontSize="small" sx={{ color: "#6B7280", verticalAlign: "middle" }} />,
  cash: <LocalAtmIcon fontSize="small" sx={{ color: "#6B7280", verticalAlign: "middle" }} />,
  web: <PublicIcon fontSize="small" sx={{ color: "#6B7280", verticalAlign: "middle" }} />,
  alreadypaid: <PointOfSaleIcon fontSize="small" sx={{ color: "#6B7280", verticalAlign: "middle" }} />,
};

const PAYMENT_METHOD_LABELS = {
  card: "Karta",
  cash: "Hotovosť",
  web: "Web",
  alreadypaid: "POS",
  free: "Zadarmo",
};

const PaymentMethodIcon = ({ methodKey }) => {
  const icon = PAYMENT_METHOD_ICONS[methodKey] || (
    <HelpOutlineIcon fontSize="small" sx={{ color: "#6B7280", verticalAlign: "middle" }} />
  );
  return icon;
};

const TodayOrders = ({ data, currencyCode }) => {
  const { t } = useTranslation();

  const isLoading = data == null;
  const todayOrders = data?.[0];
  const todayRevenue = data?.[1];
  const paymentMethodSplit = data?.[2];

  return (
    <>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.todayOrders.title")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : !todayOrders ? (
            <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="today orders">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {t("widgets.todayOrders.columns.todayOrders") || "Today"}
                    </TableCell>
                    <TableCell align="right">{todayOrders.value}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("widgets.todayOrders.columns.yesterdayOrders") ||
                        "Yesterday"}
                    </TableCell>
                    <TableCell align="right">{todayOrders.previous}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("widgets.todayOrders.columns.deltaOrders") || "Delta"}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        color={
                          todayOrders?.delta?.direction === "up"
                            ? "success"
                            : todayOrders?.delta?.direction === "down"
                            ? "error"
                            : "default"
                        }
                        label={`${todayOrders?.delta?.abs} (${todayOrders?.delta?.pct}%)`}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.todayOrders.todayRevenue")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          <Stack sx={{ width: "100%" }}>
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : !todayRevenue ? (
              <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="today revenue">
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {t("widgets.todayOrders.columns.todayRevenue") ||
                          "Today"}
                      </TableCell>
                      <TableCell align="right">
                        {Number(todayRevenue.value).toFixed(2)} {currencyCode}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("widgets.todayOrders.columns.yesterdayRevenue") ||
                          "Yesterday"}
                      </TableCell>
                      <TableCell align="right">
                        {Number(todayRevenue.previous).toFixed(2)}{" "}
                        {currencyCode}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t("widgets.todayOrders.columns.deltaRevenue") ||
                          "Delta"}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color={
                            todayRevenue?.delta?.direction === "up"
                              ? "success"
                              : todayRevenue?.delta?.direction === "down"
                              ? "error"
                              : "default"
                          }
                          label={`${todayRevenue?.delta?.abs} ${currencyCode} (${todayRevenue?.delta?.pct}%)`}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.todayOrders.paymentMethodSplit", { defaultValue: "Tržba podľa platby" })}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : !paymentMethodSplit || !Array.isArray(paymentMethodSplit.today) || paymentMethodSplit.today.length === 0 ? (
            <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="payment method split">
                <TableBody>
                  {paymentMethodSplit.today
                    .slice()
                    .sort((a, b) => b.revenue - a.revenue)
                    .map((item) => {
                      const label =
                        PAYMENT_METHOD_LABELS[item.key] ||
                        t(`pages.dsokOrders.paymentMethods.${item.key}`, { defaultValue: item.key });
                      return (
                        <TableRow key={item.key}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PaymentMethodIcon methodKey={item.key} />
                              <span>{label}</span>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`${item.count} obj.`}>
                              <span>
                                {Number(item.revenue).toFixed(2)} {currencyCode}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                            {item.count}×
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          )}
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6} lg={12}></Grid>
    </>
  );
};

export default TodayOrders;
